import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  FileText,
  FolderOpen,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { ClaraCard } from '../layout/ClaraLayoutShell';
import { masterSupabase } from '../../lib/supabase-master.js';
import TerminationCaseWorkbench from './TerminationCaseWorkbench';

const STATUS_LABELS = {
  blocked: 'Klärung erforderlich',
  awaiting_approval: 'Freigabe erforderlich',
  ready: 'Vorbereitet',
  open: 'Offen',
};

const ActionDashboardWidget = ({ selectedProperty }) => {
  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState('');
  const [openingEvidence, setOpeningEvidence] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await masterSupabase.auth.getSession();
    if (!session?.access_token) throw new Error('Keine aktive Clara-Sitzung');
    return {
      Authorization: `Bearer ${session.access_token}`,
      'x-supabase-access-token': session.access_token,
    };
  }, []);

  const fetchActions = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const response = await fetch('/api/actions/today', { headers });
      if (!response.ok) throw new Error(`Aktionsabruf fehlgeschlagen (${response.status})`);
      const data = await response.json();
      setActions(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error('Failed to fetch canonical Clara actions:', requestError);
      setError(requestError.message || 'Clara-Fälle konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchActions();
    const refreshTimer = window.setInterval(() => fetchActions({ quiet: true }), 60000);
    return () => window.clearInterval(refreshTimer);
  }, [fetchActions]);

  const filteredActions = useMemo(() => {
    const propertyId = selectedProperty?.id || selectedProperty || null;
    if (!propertyId) return actions;
    return actions.filter((action) => !action.property_id || String(action.property_id) === String(propertyId));
  }, [actions, selectedProperty]);

  const completeAction = async (action) => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`/api/actions/${action.id}/complete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Aktion konnte nicht abgeschlossen werden (${response.status})`);
      }
      setActions((current) => current.filter((item) => item.id !== action.id));
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const openEvidence = async (action) => {
    if (!action.evidence_url) return;
    setOpeningEvidence(action.id);
    setError('');
    try {
      const headers = await authHeaders();
      const response = await fetch(action.evidence_url, { headers });
      if (!response.ok) throw new Error(`Originalnachweis nicht verfügbar (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `clara-evidence-${action.evidence_id || action.case_id}.eml`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setOpeningEvidence(null);
    }
  };

  const visibleActions = filteredActions.slice(0, 5);
  const criticalCount = filteredActions.filter((action) => action.priority === 'critical').length;
  const blockedCount = filteredActions.filter((action) => action.status === 'blocked').length;

  if (isLoading) {
    return (
      <ClaraCard title="Heute handeln" icon={<Bell className="h-5 w-5 text-blue-500" />}>
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500" />
        </div>
      </ClaraCard>
    );
  }

  return (
    <>
    <ClaraCard
      title={(
        <div className="flex w-full cursor-pointer items-center justify-between" onClick={() => setIsExpanded((value) => !value)}>
          <div className="flex items-center">
            <span>Heute handeln</span>
            {filteredActions.length > 0 && (
              <span className="ml-2 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                {filteredActions.length} offen
              </span>
            )}
          </div>
          <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      )}
      className={filteredActions.length > 0 ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-green-500'}
    >
      {isExpanded && (
        <div className="p-4">
          {error && (
            <div className="mb-3 flex items-start justify-between rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <span>{error}</span>
              <button type="button" onClick={() => fetchActions()} className="ml-3 inline-flex items-center font-medium">
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Erneut laden
              </button>
            </div>
          )}

          {filteredActions.length === 0 ? (
            <div className="py-6 text-center text-gray-500">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-green-500" />
              <p>Keine offenen Clara-Fälle.</p>
              <p className="mt-1 text-sm">Dashboard, Briefing und Clara-Chat verwenden denselben Case-Bestand.</p>
            </div>
          ) : (
            <>
              {(criticalCount > 0 || blockedCount > 0) && (
                <div className="mb-4 flex items-center rounded-md bg-amber-50 p-2 text-sm font-medium text-amber-800">
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  {criticalCount > 0 ? `${criticalCount} kritische Fälle` : `${blockedCount} Fälle benötigen eine eindeutige Zuordnung`}
                </div>
              )}

              <div className="space-y-3">
                {visibleActions.map((action) => {
                  const blocked = action.status === 'blocked';
                  const canComplete = !blocked && !action.requires_approval;
                  return (
                    <div key={action.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900">{action.title}</p>
                          <p className="mt-0.5 text-xs text-gray-500">Fall {String(action.case_id).slice(0, 8)} · {action.case_type}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          blocked ? 'bg-amber-100 text-amber-900' : action.requires_approval ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {STATUS_LABELS[action.status] || action.status}
                        </span>
                      </div>

                      <p className="mt-2 text-sm text-gray-700">{action.message}</p>
                      {action.blocker_detail && (
                        <div className="mt-2 flex items-start rounded-md bg-white p-2 text-xs text-amber-800">
                          <ShieldAlert className="mr-1.5 mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span><strong>{action.blocker_code}:</strong> {action.blocker_detail}</span>
                        </div>
                      )}

                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        {action.evidence_subject && <span>Betreff: {action.evidence_subject}</span>}
                        {action.source_account && <span>Quelle: {action.source_account}</span>}
                        {action.evidence_received_at && (
                          <span className="inline-flex items-center"><Clock3 className="mr-1 h-3 w-3" />{new Date(action.evidence_received_at).toLocaleString('de-DE')}</span>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-3">
                        {['LEASE_TERMINATION', 'VACANCY_MARKETING'].includes(action.case_type) && (
                          <button
                            type="button"
                            onClick={() => setSelectedCaseId(action.case_id)}
                            className="inline-flex items-center text-xs font-semibold text-violet-700 hover:text-violet-900"
                          >
                            <FolderOpen className="mr-1 h-3.5 w-3.5" /> {action.case_type === 'VACANCY_MARKETING' ? 'Vermarktung bearbeiten' : 'Fall bearbeiten'}
                          </button>
                        )}
                        {action.evidence_url && (
                          <button
                            type="button"
                            onClick={() => openEvidence(action)}
                            disabled={openingEvidence === action.id}
                            className="inline-flex items-center text-xs font-medium text-blue-700 hover:text-blue-900 disabled:opacity-50"
                          >
                            <FileText className="mr-1 h-3.5 w-3.5" />
                            {openingEvidence === action.id ? 'Original wird geladen' : 'Original-E-Mail'}
                          </button>
                        )}
                        {canComplete && (
                          <button
                            type="button"
                            onClick={() => completeAction(action)}
                            className="inline-flex items-center text-xs font-medium text-green-700 hover:text-green-900"
                          >
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Erledigt
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredActions.length > visibleActions.length && (
                <p className="mt-3 text-center text-xs text-gray-500">
                  Weitere {filteredActions.length - visibleActions.length} offene Fälle sind über Clara abrufbar.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </ClaraCard>
    {selectedCaseId && (
      <TerminationCaseWorkbench
        caseId={selectedCaseId}
        authHeaders={authHeaders}
        onClose={() => setSelectedCaseId(null)}
        onChanged={() => fetchActions({ quiet: true })}
      />
    )}
    </>
  );
};

export default ActionDashboardWidget;
